import { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { LineSeriesOption } from "echarts";
import ReactEcharts, { EChartsOption } from "echarts-for-react";
import { useAppSelector } from "../../../../app/hooks";
import { isContrastMode } from "../../../../app/contrastSlice";
import {
	extendedArray,
	extendedSchoolMonths,
	getInterval,
	getMaxValue,
	schoolMonths,
	tooltipMark,
} from "../../shared/reportUtil";
import { ReportLevel } from "../adminReportsApiSlice";
import { AverageMonthlyScoresRow } from "./adminProgressReportApiSlice";

export function AverageMonthlyScoresChart(props: {
	level: ReportLevel;
	trait: number;
	totals: AverageMonthlyScoresRow;
	hasData: boolean;
}) {
	const chartRef = useRef<ReactEcharts>(null);
	const { foregroundColor, chartPalette, accentColor } = useAppSelector(
		(state) => state.contrast
	);

	const dataArray = schoolMonths.map(
		(month) =>
			props.totals[month.value.toLowerCase() as keyof AverageMonthlyScoresRow] as number
	);
	const actualMax = Math.max(...dataArray.filter((val) => val != null && !isNaN(val)));

	const initialEChartOptions: EChartsOption = {
		textStyle: {
			color: foregroundColor,
			fontFamily: "Montserrat",
			fontWeight: "500",
		},
		title: {
			text: `${props.level} Average Scores`,
			left: "center",
			textStyle: { fontSize: 16, fontWeight: 600, color: foregroundColor },
		},
		grid: {
			containLabel: true,
			right: 40,
			left: 40,
			top: 60,
			bottom: 25,
		},
		xAxis: {
			type: "category",
			data: extendedSchoolMonths,
			boundaryGap: false,
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
		},
		yAxis: {
			type: "value",
			min: 0,
			max: getMaxValue(props.trait, actualMax),
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
			name: "Average Score",
			nameLocation: "middle",
			nameGap: 40,
			splitLine: {
				show: true,
				lineStyle: {
					color: accentColor,
				},
			},
		},
		tooltip: {
			trigger: "axis",
			formatter: (
				params: {
					axisValue: string;
					dataIndex: number;
					color: string;
					seriesName: string;
					value: string;
				}[]
			) => {
				if (!params.find((p) => p.value)) return;

				const label = `<div>${extendedSchoolMonths[params[0].dataIndex].name}</div>`;
				const lineItem = (p: any) => {
					return p.value
						? `<div>${tooltipMark(p.color)} ${p.seriesName}: <b>${p.value}</b></div>`
						: "";
				};
				const content = params.reduce((a: string, v: any) => a + lineItem(v), label);

				return content;
			},
			textStyle: {
				fontSize: 12,
			},
		},
		animationDurationUpdate: 500,
		series: [],
	};

	useEffect(() => {
		const options = updateOptions(
			initialEChartOptions,
			getInterval(props.trait, actualMax),
			getMaxValue(props.trait, actualMax),
			getSeriesData(dataArray, foregroundColor, isContrastMode())
		);

		if (chartPalette != undefined) {
			options.color = chartPalette;
		}

		chartRef.current?.getEchartsInstance().setOption(options, true);
	}, [props.totals, props.trait, chartPalette, foregroundColor, accentColor]);

	return (
		<>
			{props.hasData && (
				<ReactEcharts
					ref={chartRef}
					option={initialEChartOptions}
					className="w-full mt-4 !h-96  p-4 border-2 border-gray-200"
				/>
			)}
		</>
	);
}

const getSeriesData = (dataArray: number[], foregroundColor: string, isContrast: boolean) =>
	buildSeriesOptions(dataArray, foregroundColor, isContrast);

function buildSeriesOptions(
	dataArray: number[],
	foregroundColor: string,
	isContrast: boolean
): LineSeriesOption {
	const extendedDataArray = extendedArray(dataArray);

	return {
		type: "line",
		name: "Average Score",
		connectNulls: false,
		data: extendedDataArray,
		label: {
			show: true,
			color: foregroundColor,
			formatter: (params: any) => {
				const val = params.value;
				const isEdge =
					params.dataIndex === 0 || params.dataIndex === extendedDataArray.length - 1;
				return isEdge || val === 0 ? "" : val;
			},
		},
		cursor: "default",
		symbol: (value, params) => {
			if (params.dataIndex === 0 || params.dataIndex === extendedDataArray.length - 1) {
				return "none";
			}
			return "emptyCircle";
		},
		symbolSize: 6,
		smooth: true,
		areaStyle: isContrast
			? undefined
			: {
					opacity: 0.8,
					color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
						{
							offset: 0,
							color: "rgb(77, 119, 255)",
						},
						{
							offset: 1,
							color: "rgb(0, 221, 255)",
						},
					]),
				},
		emphasis: {
			scale: 1.3,
			itemStyle: {
				shadowBlur: 10,
				shadowColor: "rgba(0, 0, 0, 0.5)",
			},
		},
	};
}

function updateOptions(
	options: EChartsOption,
	yAxisInterval: number,
	yAxisMax: number,
	seriesData: LineSeriesOption
): EChartsOption {
	options.yAxis.interval = yAxisInterval;
	options.yAxis.max = yAxisMax;
	options.series = seriesData;
	return options;
}
